import { useMemo, useState } from 'react';

import { Alert, Button, Form, Input, Modal, Space, Typography } from 'antd';
import dayjs from 'dayjs';

import type { ContactChannel } from '@store/slices/userSlice';
import { CodeInput } from '@tools/components/commonFields.tsx';

export type ContactVerificationModalProps = {
  open: boolean;
  channel: ContactChannel | null;
  maskedTarget?: string;
  expiresAt?: string;
  status: 'idle' | 'requesting' | 'awaiting' | 'verifying' | 'error';
  error?: string | null;
  remainingAttempts?: number | null;
  retryAfter?: number | null;
  onConfirm: (code: string) => Promise<unknown>;
  onCancel: () => void;
};

export const ContactVerificationModal = ({
  open,
  channel,
  maskedTarget,
  expiresAt,
  status,
  error,
  remainingAttempts,
  retryAfter,
  onConfirm,
  onCancel,
}: ContactVerificationModalProps) => {
  const [form] = Form.useForm<{ code: string }>();
  const [resetCodeInput, setResetCodeInput] = useState(false);
  const loading = status === 'verifying';
  const targetLabel = useMemo(() => {
    if (channel === 'email') return 'correo electrónico';
    if (channel === 'phone') return 'número de teléfono';
    return 'contacto';
  }, [channel]);

  const expiresLabel = useMemo(() => {
    if (!expiresAt) return null;
    const formatted = dayjs(expiresAt).format('HH:mm');
    return `El código vence a las ${formatted}.`;
  }, [expiresAt]);

  const handleFinish = async ({ code }: { code: string }) => {
    try {
      await onConfirm(code);
      form.resetFields();
      setResetCodeInput(true);
    } catch {
      /* La UI mostrará el error desde el store */
    }
  };

  const handleCodeChange = (code: string) => {
    form.setFieldValue('code', code);
  };

  return (
    <Modal
      open={open}
      onCancel={onCancel}
      title={`Confirma tu ${targetLabel}`}
      footer={null}
      destroyOnHidden
      centered
    >
      <Space orientation="vertical" size="middle" style={{ width: '100%' }}>
        <Typography.Paragraph>
          Ingresá el código de 6 dígitos que enviamos a <strong>{maskedTarget ?? 'tu contacto'}</strong>.
        </Typography.Paragraph>
        {expiresLabel && (
          <Typography.Paragraph type="secondary" style={{ marginBottom: 0 }}>
            {expiresLabel}
          </Typography.Paragraph>
        )}
        {typeof remainingAttempts === 'number' && (
          <Typography.Paragraph type="secondary" style={{ marginBottom: 0 }}>
            Intentos restantes: {remainingAttempts}
          </Typography.Paragraph>
        )}
        {retryAfter && (
          <Alert
            type="warning"
            showIcon
            title={`Debes esperar ${retryAfter} segundos antes de solicitar un nuevo código.`}
          />
        )}
        {error && (
          <Alert type="error" showIcon title={error} />
        )}
        <Form form={form} layout="vertical" onFinish={handleFinish}>
          <Form.Item
            label="Código de verificación"
            required
            htmlFor="contact-verification-code-0"
          >
            <CodeInput
              digits={6}
              inputIdPrefix="contact-verification-code"
              inputAriaLabel={(index: number) => `Dígito ${index + 1} del código de verificación`}
              digitBox={{ style: { marginRight: 5, width: 44 } }}
              onChange={handleCodeChange}
              onComplete={handleCodeChange}
              resetCode={resetCodeInput}
              setResetCode={setResetCodeInput}
              disabled={loading}
            />
          </Form.Item>
          <Form.Item
            name="code"
            hidden
            rules={[
              { required: true, message: 'Ingresá el código recibido' },
              { len: 6, message: 'Ingresá los 6 dígitos del código' },
            ]}
          >
            <Input />
          </Form.Item>
          <Button type="primary" htmlType="submit" block loading={loading} disabled={!channel}>
            Confirmar
          </Button>
        </Form>
      </Space>
    </Modal>
  );
};

export default ContactVerificationModal;
